import { apiClient } from "@/lib/api-client";
import type { ContactList, ContactListType, PagedResult } from "@/types/email";
import type { Contact, ContactListFull, CreateContactListPayload, UpdateContactListPayload } from "@/types/contacts";

interface PagedContactLists {
  items: ContactList[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export type { CreateContactListPayload, UpdateContactListPayload };

class ContactListService {
  async getContactLists(
    accountId: string,
    params?: { page?: number; pageSize?: number; listType?: ContactListType; isActive?: boolean },
  ): Promise<PagedContactLists> {
    const queryParams: Record<string, string | number | boolean> = {};
    if (params?.page !== undefined) queryParams.page = params.page;
    if (params?.pageSize !== undefined) queryParams.pageSize = params.pageSize;
    if (params?.listType !== undefined) queryParams.listType = params.listType;
    if (params?.isActive !== undefined) queryParams.isActive = params.isActive;

    return apiClient.get(`/accounts/${accountId}/contact-lists`, {
      requiresAuth: true,
      params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    });
  }

  async getList(accountId: string, listId: string): Promise<ContactListFull> {
    return apiClient.get(`/accounts/${accountId}/contact-lists/${listId}`, { requiresAuth: true });
  }

  async createList(accountId: string, data: CreateContactListPayload): Promise<ContactListFull> {
    return apiClient.post(`/accounts/${accountId}/contact-lists`, data, { requiresAuth: true });
  }

  async updateList(accountId: string, listId: string, data: UpdateContactListPayload): Promise<ContactListFull> {
    return apiClient.patch(`/accounts/${accountId}/contact-lists/${listId}`, data, { requiresAuth: true });
  }

  async deleteList(accountId: string, listId: string): Promise<void> {
    return apiClient.delete(`/accounts/${accountId}/contact-lists/${listId}`, { requiresAuth: true });
  }

  async getListContacts(
    accountId: string,
    listId: string,
    params?: { page?: number; pageSize?: number },
  ): Promise<PagedResult<Contact>> {
    const q: Record<string, string | number> = {};
    if (params?.page !== undefined) q.page = params.page;
    if (params?.pageSize !== undefined) q.pageSize = params.pageSize;

    return apiClient.get(`/accounts/${accountId}/contact-lists/${listId}/contacts`, {
      requiresAuth: true,
      params: Object.keys(q).length > 0 ? q : undefined,
    });
  }

  async addContactsToList(accountId: string, listId: string, contactIds: string[]): Promise<void> {
    return apiClient.post(
      `/accounts/${accountId}/contact-lists/${listId}/contacts`,
      { contactIds },
      { requiresAuth: true },
    );
  }

  async removeContactsFromList(accountId: string, listId: string, contactIds: string[]): Promise<void> {
    return apiClient.post(
      `/accounts/${accountId}/contact-lists/${listId}/contacts/remove`,
      { contactIds },
      { requiresAuth: true },
    );
  }
}

export const contactListService = new ContactListService();
